import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Box, Button, CircularProgress, IconButton, Paper, Tooltip, Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import DatabaseIcon from '@mui/icons-material/Storage'
import BoltIcon from '@mui/icons-material/Bolt'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  useBackfillProshipApiMutation, useGetConnectorStatusQuery, useListIngestRunsQuery, useSyncHubspotIncrementalMutation,
  useSyncHubspotMutation, useSyncLinkedInAbmMutation, useSyncProshipApiMutation, usePreflightHubspotMutation,
} from '../../redux/growthosConnectorsApiSlice'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { GX_BORDER, GX_INK_500, GX_INK_900, GX_CARD_SX, GX_PAGE_SX } from '../growthos-shared/GrowthOsUi'
import { GxPagedTable } from '../growthos-shared/GxPagedTable'

const INK_400 = '#94a3b8'
const fmtWhen = (iso) => (iso
  ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : null)
const shortRunId = (id) => (id ? `#${String(id).slice(-6)}` : '')

// "Last synced" line under each connector card — its own last successful run, plus the latest
// run when that one is still running or failed (so a failure is never hidden behind an older success).
function LastSync({ status }) {
  const ok = status?.lastSuccess
  const last = status?.lastRun
  const ageHours = ok?.finishedAt ? (Date.now() - new Date(ok.finishedAt).getTime()) / 3600000 : Infinity
  const color = ageHours === Infinity ? '#e11d48' : ageHours > 48 ? '#b45309' : '#059669'
  return (
    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '9999px',
        border: `1px solid ${color}4d`, color, fontSize: 11.5, fontWeight: 600,
      }}>
        <RefreshIcon sx={{ fontSize: 13 }} />
        {ok ? `Last synced: ${fmtWhen(ok.finishedAt || ok.startedAt)}` : 'Never synced'}
        {ok && <Box component="span" sx={{ fontWeight: 400, opacity: 0.8 }}>· {ok.mode || 'run'} {shortRunId(ok.id)} · {Number(ok.rowsUpserted || 0).toLocaleString('en-IN')} upserted</Box>}
      </Box>
      {last && last.id !== ok?.id && last.status !== 'SUCCESS' && (
        <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: last.status === 'FAILED' ? '#e11d48' : '#4f46e5' }}>
          Latest run {shortRunId(last.id)} {last.status === 'RUNNING' ? `in progress since ${fmtWhen(last.startedAt)}` : `failed ${fmtWhen(last.finishedAt || last.startedAt)}`}
        </Box>
      )}
    </Box>
  )
}

function ConnectorCard({ icon, title, subtitle, description, children, message, status, footer }) {
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: GX_INK_900 }}>
          {title} <Box component="span" sx={{ fontSize: 12, fontWeight: 400, color: INK_400 }}>· {subtitle}</Box>
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5 }}>{description}</Typography>
      <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>{children}</Box>
      {status !== undefined && <LastSync status={status} />}
      {message && (
        <Box sx={{
          mt: 1.5, p: 1.5, borderRadius: '10px', fontSize: 13,
          border: '1px solid', borderColor: message.kind === 'ok' ? 'rgba(5,150,105,.3)' : 'rgba(225,29,72,.3)',
          bgcolor: message.kind === 'ok' ? 'rgba(5,150,105,.06)' : 'rgba(225,29,72,.06)',
          color: message.kind === 'ok' ? '#047857' : '#be123c',
        }}>
          {message.text}
        </Box>
      )}
      {footer}
    </Paper>
  )
}

const statusIcon = (status) => {
  if (status === 'SUCCESS') return <CheckCircleIcon sx={{ fontSize: 14, color: '#059669' }} />
  if (status === 'FAILED') return <CancelIcon sx={{ fontSize: 14, color: '#e11d48' }} />
  return <HourglassTopIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
}

const spinner = <CircularProgress size={14} sx={{ color: 'inherit' }} />
const PAGE_SIZE = 50

export default function ConnectorsPage() {
  const [page, setPage] = useState(0)
  const { data: runsData, isLoading: runsLoading } = useListIngestRunsQuery({ page, size: PAGE_SIZE }, { pollingInterval: 10000 })
  const { data: statusData } = useGetConnectorStatusQuery(undefined, { pollingInterval: 10000 })
  const [syncProshipApi, proshipSyncState] = useSyncProshipApiMutation()
  const [backfillProshipApi, proshipBackfillState] = useBackfillProshipApiMutation()
  const [syncHubspot, hubspotSyncState] = useSyncHubspotMutation()
  const [syncHubspotIncremental, hubspotIncState] = useSyncHubspotIncrementalMutation()
  const [preflightHubspot, preflightState] = usePreflightHubspotMutation()
  // Reference: Full sync stays disabled until "Test connection" has passed in this session.
  const [preflightOk, setPreflightOk] = useState(false)
  const [syncLinkedInAbm, linkedInAbmSyncState] = useSyncLinkedInAbmMutation()

  const [proshipMessage, setProshipMessage] = useState(null)
  const [hubspotMessage, setHubspotMessage] = useState(null)
  const [emailMessage, setEmailMessage] = useState(null)
  const [linkedInAbmMessage, setLinkedInAbmMessage] = useState(null)

  const runs = runsData?.runs || []
  const total = runsData?.total || 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE)

  const conn = statusData?.connectors || {}
  const running = (key) => (conn[key]?.lastRun?.status === 'RUNNING' ? conn[key].lastRun : null)
  const activeProship = running('proship_api')
  const activeHubspot = running('hubspot')
  const activeAbm = running('linkedin_abm_lemlist')
  const untaggedCount = statusData?.untaggedBrandCount || 0
  const hubspotBusy = hubspotSyncState.isLoading || hubspotIncState.isLoading || !!activeHubspot

  const handleProshipSync = async () => {
    setProshipMessage(null)
    setPage(0)
    try {
      const run = await syncProshipApi().unwrap()
      setProshipMessage({ kind: 'ok', text: `Synced — ${run.rowsRead} merchant-day rows rolled up (run ${shortRunId(run.id)}).` })
    } catch (e) {
      setProshipMessage({ kind: 'err', text: e?.data?.message || e?.message || 'Sync failed.' })
    }
  }
  const handleProshipBackfill = async () => {
    setProshipMessage(null)
    setPage(0)
    try {
      const run = await backfillProshipApi(24).unwrap()
      setProshipMessage({ kind: 'ok', text: `Backfilled last 24 months — ${run.rowsRead} merchant-day rows rolled up (run ${shortRunId(run.id)}).` })
    } catch (e) {
      setProshipMessage({ kind: 'err', text: e?.data?.message || e?.message || 'Backfill failed.' })
    }
  }
  const runHubspot = async (incremental, setMessage, label) => {
    setMessage(null)
    setPage(0)
    try {
      const run = await (incremental ? syncHubspotIncremental() : syncHubspot()).unwrap()
      setMessage({ kind: 'ok', text: `${label} ${shortRunId(run.id)} completed (${run.mode}) — read ${run.rowsRead}, upserted ${run.rowsUpserted}, errored ${run.rowsErrored}.` })
    } catch (e) {
      setMessage({ kind: 'err', text: e?.data?.message || e?.message || 'Sync failed. Check that a HubSpot API token is configured.' })
    }
  }
  const handlePreflight = async () => {
    setHubspotMessage(null)
    try {
      const d = await preflightHubspot().unwrap()
      setPreflightOk(true)
      setHubspotMessage({ kind: 'ok', text: `Connection verified — ${d.pipelineCount} pipelines, ${d.stageCount} stages; contacts, companies, deals and owners are readable. No data was imported.` })
    } catch (e) {
      setPreflightOk(false)
      setHubspotMessage({ kind: 'err', text: e?.data?.error || e?.data?.message || e?.message || 'HubSpot preflight failed.' })
    }
  }
  const handleLinkedInAbmSync = async () => {
    setLinkedInAbmMessage(null)
    setPage(0)
    try {
      const run = await syncLinkedInAbm().unwrap()
      setLinkedInAbmMessage({ kind: 'ok', text: `LinkedIn/ABM sync ${shortRunId(run.id)} completed — ${run.rowsRead} campaigns read, ${run.rowsUpserted} stats + new replies upserted.` })
    } catch (e) {
      setLinkedInAbmMessage({ kind: 'err', text: e?.data?.message || e?.message || 'Sync failed. Check that a lemlist API token is configured.' })
    }
  }

  const btnSx = { textTransform: 'none', borderRadius: '10px' }
  const runColumns = [
    { key: 'run', header: 'Run', cell: (r) => shortRunId(r.id) },
    { key: 'connector', header: 'Connector', cell: (r) => <>{r.connectorKey} <Box component="span" sx={{ color: INK_400 }}>· {r.mode}</Box></> },
    { key: 'status', header: 'Status', cell: (r) => <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>{statusIcon(r.status)}{r.status}</Box> },
    { key: 'read', header: 'Read', align: 'right', cell: (r) => Number(r.rowsRead || 0).toLocaleString('en-IN') },
    { key: 'upserted', header: 'Upserted', align: 'right', cell: (r) => Number(r.rowsUpserted || 0).toLocaleString('en-IN') },
    { key: 'errored', header: 'Errored', align: 'right', cell: (r) => <Box component="span" sx={{ fontWeight: r.rowsErrored > 0 ? 700 : 400, color: r.rowsErrored > 0 ? '#e11d48' : undefined }}>{r.rowsErrored}</Box> },
    { key: 'by', header: 'By', cell: (r) => r.triggeredBy || '—' },
    { key: 'started', header: 'Started', cell: (r) => (r.startedAt ? new Date(r.startedAt).toLocaleString() : '—') },
    { key: 'finished', header: 'Finished', cell: (r) => (r.finishedAt ? new Date(r.finishedAt).toLocaleString() : '—') },
  ]

  return (
    <Box sx={GX_PAGE_SX}>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>Connectors</Typography>
      <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5, mb: 3, maxWidth: 700 }}>
        Plug-and-play data sources. Every run is ledgered; nothing is dropped silently.
      </Typography>

      <ConnectorCard
        icon={<DatabaseIcon sx={{ fontSize: 18, color: '#4f46e5' }} />}
        title="ProShip" subtitle="Shipments & Revenue (API)"
        description="Rolls up shipment counts and revenue directly from live ProShip order data (MongoOrder), per merchant per day, joined to brands via ProShip Merchant ID. Auto-syncs twice a day (~2:10am/2:10pm IST, self-healing the last 3 days each time) via the ProShip rollup cron; use the buttons below to force a run now."
        message={proshipMessage}
        status={conn.proship_api}
        footer={untaggedCount > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '12px', border: '1px solid #fde68a', bgcolor: '#fffbeb', color: '#92400e', fontSize: 13 }}>
            <LocalOfferIcon sx={{ fontSize: 18, color: '#d97706' }} />
            <Box component="span" sx={{ flex: 1, minWidth: 220 }}>
              <strong>{untaggedCount}</strong> brand{untaggedCount === 1 ? ' is' : 's are'} not yet tagged <strong>SME</strong> or <strong>Enterprise</strong>.
              New brands arrive from a ProShip sync unclassified — tag them so SME scoping and dashboards are accurate.
            </Box>
            <Link to="/growthos/brands" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 8, background: '#d97706', color: '#fff', padding: '6px 12px', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
              Tag in Data → Brands <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </Link>
          </Box>
        )}
      >
        <Button size="small" variant="contained" startIcon={proshipSyncState.isLoading || activeProship ? spinner : <RefreshIcon />} disabled={proshipSyncState.isLoading || proshipBackfillState.isLoading || !!activeProship} onClick={handleProshipSync} sx={btnSx}>
          {activeProship ? `Run ${shortRunId(activeProship.id)} in progress` : proshipSyncState.isLoading ? 'Syncing…' : 'Sync now (last 3 days)'}
        </Button>
        <Button size="small" variant="outlined" disabled={proshipBackfillState.isLoading || proshipSyncState.isLoading || !!activeProship} onClick={handleProshipBackfill} sx={btnSx}>
          {proshipBackfillState.isLoading ? 'Backfilling…' : 'Backfill last 24 months'}
        </Button>
      </ConnectorCard>

      <ConnectorCard
        icon={<BoltIcon sx={{ fontSize: 18, color: '#f97316' }} />}
        title="HubSpot Activity" subtitle="CRM (owners, pipelines, contacts, deals)"
        description="Full CRM sync — owners (POC names), pipelines & stages, contacts (= leads) and deals (incl. MBR fields) across all pipelines. Incremental sync pulls only records changed since the last successful run (falls back to a full sync when there is none). Requires a configured HubSpot API token."
        message={hubspotMessage}
        status={conn.hubspot}
      >
        <Button size="small" variant="outlined" startIcon={preflightState.isLoading ? spinner : <VerifiedUserIcon />} disabled={preflightState.isLoading || hubspotSyncState.isLoading} onClick={handlePreflight} sx={btnSx}>
          {preflightState.isLoading ? 'Testing…' : 'Test connection'}
        </Button>
        <Tooltip title={preflightOk ? '' : 'Test the connection first'}>
          <span>
            <Button size="small" variant="contained" startIcon={hubspotSyncState.isLoading || activeHubspot ? spinner : <BoltIcon />} disabled={!preflightOk || hubspotBusy || preflightState.isLoading} onClick={() => runHubspot(false, setHubspotMessage, 'HubSpot full sync')} sx={btnSx}>
              {activeHubspot ? `Run ${shortRunId(activeHubspot.id)} in progress` : hubspotSyncState.isLoading ? 'Syncing…' : 'Full sync (Activity)'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Delta sync — only records changed since the last successful sync. Much faster than a full rebuild.">
          <span>
            <Button size="small" variant="outlined" startIcon={hubspotIncState.isLoading ? spinner : <RefreshIcon />} disabled={hubspotBusy} onClick={() => runHubspot(true, setHubspotMessage, 'HubSpot incremental sync')} sx={btnSx}>
              {activeHubspot ? 'Sync in progress' : hubspotIncState.isLoading ? 'Syncing…' : 'Incremental sync'}
            </Button>
          </span>
        </Tooltip>
      </ConnectorCard>

      {/* HubSpot Email — the original runs this as its own connector; here the email-engagement
          counters ride on the HubSpot contact sync, so this card reports that sync's status and
          triggers it, rather than pretending a separate email connector exists. */}
      <ConnectorCard
        icon={<MailOutlineIcon sx={{ fontSize: 18, color: '#f97316' }} />}
        title="HubSpot Email" subtitle="email-outreach contacts"
        description={(
          <>
            The &ldquo;ever emailed&rdquo; contact universe
            {statusData?.emailContacts != null && <> (<strong>{Number(statusData.emailContacts).toLocaleString('en-IN')}</strong> contacts delivered or bounced)</>}
            {' '}with engagement counts (delivered / opened / clicked / replied / bounced) and attribution. These counters are
            pulled as part of the HubSpot contact sync, so they&rsquo;re as fresh as the last HubSpot run below; syncing here runs that sync.
          </>
        )}
        message={emailMessage}
        status={conn.hubspot}
      >
        <Button size="small" variant="contained" startIcon={hubspotIncState.isLoading || activeHubspot ? spinner : <RefreshIcon />} disabled={hubspotBusy} onClick={() => runHubspot(true, setEmailMessage, 'HubSpot contact + email sync')} sx={btnSx}>
          {activeHubspot ? `Run ${shortRunId(activeHubspot.id)} in progress` : hubspotIncState.isLoading ? 'Syncing…' : 'Sync email contacts'}
        </Button>
        <Link to="/growthos/email-marketing" style={{ alignSelf: 'center', fontSize: 13, color: '#4338ca', textDecoration: 'none' }}>
          Open Email Marketing →
        </Link>
      </ConnectorCard>

      <ConnectorCard
        icon={<LinkedInIcon sx={{ fontSize: 18, color: '#0A66C2' }} />}
        title="LinkedIn/ABM" subtitle="lemlist outreach automation"
        description="Live from lemlist — campaigns, batch stats (Programme Totals, Leaderboard by Sales Leader), and first-reply text per lead conversation (never the full thread). Requires a configured lemlist API token."
        message={linkedInAbmMessage}
        status={conn.linkedin_abm_lemlist}
      >
        <Button size="small" variant="contained" startIcon={linkedInAbmSyncState.isLoading || activeAbm ? spinner : <RefreshIcon />} disabled={linkedInAbmSyncState.isLoading || !!activeAbm} onClick={handleLinkedInAbmSync} sx={btnSx}>
          {activeAbm ? `Run ${shortRunId(activeAbm.id)} in progress` : linkedInAbmSyncState.isLoading ? 'Syncing…' : 'Full sync (ABM)'}
        </Button>
      </ConnectorCard>

      <Paper variant="outlined" sx={{ ...GX_CARD_SX, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: GX_INK_500 }}>
            Ingest Runs
          </Typography>
          {total > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>
                {rangeStart}–{rangeEnd} of {total.toLocaleString('en-IN')}
              </Typography>
              <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} aria-label="Previous page">
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} aria-label="Next page">
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
        {runsLoading ? (
          <Typography sx={{ fontSize: 13, color: GX_INK_500, px: 2.5, pb: 2.5 }}>Loading…</Typography>
        ) : !runs.length ? (
          <Typography sx={{ fontSize: 13, color: GX_INK_500, px: 2.5, pb: 3, borderTop: `1px solid ${GX_BORDER}`, pt: 2 }}>
            No runs yet — trigger the first sync above.
          </Typography>
        ) : (
          // Scrolls inside the card (sticky header, capped height); paging is server-side above.
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <GxPagedTable variant="poc" rows={runs} rowKey={(r) => r.id} columns={runColumns} paginate={false} serial={false} maxBodyHeight={560} />
          </Box>
        )}
      </Paper>
    </Box>
  )
}
